import { scrubSentryEvent } from '@coreltb/shared/lib';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AppToaster } from './components/ui/AppToaster';
import { DailyWelcome } from './features/welcome/DailyWelcome';
import { BreakpointProvider } from './hooks/useBreakpoint';
import { AppRouter } from './router';
// Czcionka z naszego serwera, nie z Google Fonts (RODO: IP uzytkownika nie trafia do Google).
import '@fontsource-variable/inter';
import './styles/globals.css';

// Sentry error tracking — inicjalizacja jak najwczesniej.
// VITE_SENTRY_DSN ustawiany w build time (.env.production lub Pages env var).
// Gdy brak DSN (lokalny dev) — Sentry sie inicjalizuje ale nie wysyla eventow.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Nie wysylamy default PII (IP/cookies) — zgodnosc z RODO + nasza paleta sanitizerow
    sendDefaultPii: false,
    // RODO B4: maskowanie PESEL/e-mail/telefon, tokeny linkow i query string (Sentry = USA).
    beforeSend: (event) => scrubSentryEvent(event),
    beforeSendTransaction: (event) => scrubSentryEvent(event),
    // 10% transactions zeby zmiescic sie w free tier (5k events/mies)
    tracesSampleRate: 0.1,
    // BrowserTracing + replays usuniete swiadomie — wlaczymy gdy bedzie potrzeba debug
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Domyslne ustawienia query — zgodnie z plan crm-leady-plan.md sekcja 5.3.
      //
      // Live updates: refetchInterval=30s + refetchOnWindowFocus=true.
      // Gdy admin zmieni cos w panelu (rola usera, nowy lead, status oferty),
      // inne otwarte zakladki widza zmiane w max 30s. Plus natychmiast przy
      // przelaczeniu na zakladke (focus). Plus mutation invalidation — gdy
      // SAM uzytkownik zmienia, jego widok odswieza sie natychmiast.
      // W tle (visibilityState=hidden) polling jest pauzowany.
      //
      // 30s zamiast 10s: 3x mniej requestow, koszt UX = max 30s opoznienie
      // dla zmian innych userow (akceptowalne dla CRM B2B).
      staleTime: 15 * 1000, // 15s
      gcTime: 5 * 60 * 1000,
      refetchInterval: 30 * 1000, // 30s polling
      refetchIntervalInBackground: false, // pauzuj gdy zakladka schowana
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Nie retry na 4xx (poza 408 Timeout)
        if (error instanceof Error && 'status' in error) {
          const status = (error as Error & { status?: number }).status;
          if (status && status >= 400 && status < 500 && status !== 408) {
            return false;
          }
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

// Auto-reload po deployu nowej wersji.
// Vite emituje `vite:preloadError` gdy lazy chunk (np. /assets/EditOfferPage-{hash}.js)
// nie da sie zaladowac — zwykle dlatego ze hash sie zmienil po nowym deployu, a user
// ma stary `index.html` w cache. Reload pobiera fresh index → nowe hashe → dziala.
//
// Sentry-friendly: logujemy jako breadcrumb przed reloadem, zeby widziec to w trace.
window.addEventListener('vite:preloadError', (event) => {
  console.warn('[vite:preloadError] new deploy detected, reloading', event);
  Sentry.addBreadcrumb({
    category: 'app.lifecycle',
    level: 'info',
    message: 'vite:preloadError → auto-reload',
  });
  // event.preventDefault() blokuje uncaught throw z Vite; reload odpalamy potem.
  event.preventDefault();
  // Bezpiecznik petli: najwyzej jedno takie odswiezenie na 10 s.
  try {
    const last = Number(sessionStorage.getItem('coreltb.preloadReload') ?? 0);
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem('coreltb.preloadReload', String(Date.now()));
  } catch {
    // sessionStorage niedostepny — przeladowujemy jak dotad
  }
  window.location.reload();
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Element #root nie znaleziony w HTML');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BreakpointProvider>
        <AppRouter />
        {/* Powitanie raz dziennie — nad AuthGuardem, od pierwszej klatki (bez mignięcia ładowania). */}
        <DailyWelcome />
        <AppToaster />
      </BreakpointProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
